import React, { useCallback, useEffect, useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import { Link, useHistory, useParams } from "react-router-dom";

import Layout from "../../../../../components/Layout";
import Container from "../../../../../components/Container";
import Flex from "../../../../../components/Flex";
import Breadcrumbs from "../../../../../components/Breadcrumbs";
import CheckBox from "../../../../../components/CheckBox";
import Content from "../../../../../components/Content";
import TextBox from "../../../../../components/TextBox";
import Label from "../../../../../components/Label";
import Button from "../../../../../components/Button";
import Tooltip from "../../../../../components/Tooltip";
import ButtonList from "../../../../../components/ButtonList";
import { useFetch } from "../../../../../components/useFetch";
import { useMessageToast } from "../../../../../components/MessageProvider";
import { useAuthState } from "../../../../../components/AuthProvider";
import errorMessageHandler from "../../../errorMessageHandler";
import Spinner from "../../../../../components/Loader/Spinner";

import styles from "./AddEditAccount.module.scss";

const AddEditAccount = () => {
  const moment = require("moment-timezone");
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const REACT_APP_DOMAIN = process.env.REACT_APP_DOMAIN;
  const orgApiUrl = `${clientConfigApiDomain}/organizations`;
  const clientsRpcUrl = `${clientConfigApiDomain}/rpc/clients`;
  const clientsApiUrl = `${clientConfigApiDomain}/clients`;
  const aggregateCountRpcUrl = `${clientConfigApiDomain}/rpc/aggregate_counts`;
  // as a result of the serializer in CCS, we need to map to the viewset in CCS
  const CCS_PATH = clientConfigApiDomain
    .split("/")
    .slice(-2)
    .join("/");
  // CCS_PATH for ex. will be /v1/config
  const hyperlinkApiUrl = `${REACT_APP_DOMAIN}/${CCS_PATH}/organizations`;

  const { id, clientId } = useParams();

  const history = useHistory();
  const { user } = useAuthState();
  const toast = useMessageToast();

  const [
    { hasLoaded: addEditHasLoaded, hasError: addEditHasError, error },
    addEditFetch
  ] = useFetch();

  const [clientData, setClientData] = useState([]);
  const [orgData, setOrgData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [allowOidAddEdit, setAllowOidAddEdit] = useState(true); // true for insert mode

  const fetchData = useCallback(async () => {
    window.scrollTo(0, 0);
    setIsLoading(true);

    if (clientId !== undefined) {
      // in edit mode
      const response = await fetch(`${clientsRpcUrl}/?id=${clientId}`);
      const data = await response.json();
      setClientData(data.results[0]);
      setMaintenanceMode(data.results[0].maintenance_mode);

      const aggregateCountResponse = await fetch(
        `${aggregateCountRpcUrl}/?client_oid=${await data.results[0].oid}`
      );
      const aggregateCountData = await aggregateCountResponse.json();

      const canOidAddEdit =
        aggregateCountData.results.total_added === 0 &&
        aggregateCountData.results.total_removed === 0 &&
        aggregateCountData.results.total_updated === 0;
      setAllowOidAddEdit(canOidAddEdit);
    }

    const orgResponse = await fetch(`${orgApiUrl}/${id}/`);
    const orgData = await orgResponse.json();
    setOrgData(orgData);

    setIsLoading(false);
    setHasLoaded(true);
  }, [clientId, clientsRpcUrl, id, orgApiUrl, aggregateCountRpcUrl]);

  React.useEffect(() => {
    if (!hasLoaded && id !== undefined) {
      fetchData();
    } else {
      // this enables the add screen to show up
      setHasLoaded(true);
    }
  }, [fetchData, hasLoaded, id]);

  useEffect(() => {
    if (clientId === undefined && addEditHasLoaded) {
      if (addEditHasLoaded) {
        toast.success(`Account added successfully.`);

        history.push(`/admin/organizations/${id}/`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error("Failed to add account");
      }
    } else {
      if (addEditHasLoaded) {
        toast.success(`Account updated successfully.`);

        history.push(`/admin/organizations/${id}/accounts/${clientId}`);
      } else if (addEditHasError) {
        if (error.response.status === 400) {
          const errorMsgArray = errorMessageHandler(error.response.data);
          errorMsgArray.forEach(msg => toast.error(msg));
        } else toast.error("Failed to update account");
      }
    }
  }, [clientId, addEditHasError, addEditHasLoaded, toast, history, id, error]);

  let loadedBreadCrumbs = "";
  if (isLoading) {
    loadedBreadCrumbs = <Spinner />;
  } else if (hasLoaded) {
    loadedBreadCrumbs = (
      <Breadcrumbs>
        <Breadcrumbs.Item render={() => <Link to="/">Home</Link>} />
        <Breadcrumbs.Item
          render={() => <Link to="/admin">Administration</Link>}
        />
        <Breadcrumbs.Item
          render={() => <Link to="/admin/organizations">Organizations</Link>}
        />
        <Breadcrumbs.Item
          render={() => (
            <Link to={`/admin/organizations/${id}`}>
              Organization: {orgData.name}
            </Link>
          )}
        />
        {clientId ? (
          <>
            <Breadcrumbs.Item
              render={() => (
                <Link to={`/admin/organizations/${id}/accounts/${clientId}`}>
                  Account: {clientData.name}
                </Link>
              )}
            />
            <Breadcrumbs.Item last render={() => "Edit"}></Breadcrumbs.Item>
          </>
        ) : (
          <Breadcrumbs.Item
            last
            render={() => "Add Account"}
          ></Breadcrumbs.Item>
        )}
      </Breadcrumbs>
    );
  }

  const isEditVisible = orgData => {
    return user.role === "Global Admin" || user.orgIds.includes(orgData.id);
  };
  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>{loadedBreadCrumbs}</Flex.Item>
          {isLoading ? (
            <Spinner />
          ) : hasLoaded ? (
            <Flex.Item span={12}>
              <Content>
                {clientId ? (
                  <h1>Edit Account: {clientData.name}</h1>
                ) : (
                  <h1>Add Account</h1>
                )}
              </Content>
              <div className={styles.base}>
                <div className={styles.detailsFlex}>
                  <h3>Account Details</h3>
                </div>
                <div className={styles.details}>
                  <Formik
                    initialValues={{
                      id: clientData.id,
                      organization: id,
                      name: clientData.name,
                      oid: clientData.oid,
                      shortCode: clientData.short_code,
                      createdBy: clientData.created_by,
                      updatedBy: clientData.updated_by,
                      maintenance_mode: clientData.maintenance_mode
                    }}
                    enableReinitialize={true}
                    validationSchema={Yup.object().shape({
                      name: Yup.string()
                        .trim()
                        .required("Name field is required")
                        .matches(
                          /^(?![=@+\-\r\n\t])(?:(?![,;:"'][=@+\-\r\n\t]).)*$/,
                          "Invalid characters pattern found in name"
                        ),
                      oid: Yup.string()
                        .trim()
                        .required("OID field is required")
                        .matches(
                          /^([0-2])((\.0)|(\.[1-9][0-9]*))*$/,
                          "Invalid OID format"
                        )
                    })}
                    onSubmit={(values, { setSubmitting, resetForm }) => {
                      setSubmitting(true);
                      let data = {
                        name: values.name,
                        oid: values.oid,
                        short_code: values.shortCode,
                        organization: `${hyperlinkApiUrl}/${id}/`,
                        updated_by: user.username,
                        created_by: values.createdBy,
                        maintenance_mode: maintenanceMode
                      };
                      let method = "";
                      let url = "";

                      if (clientId) {
                        data["id"] = clientId;
                        method = "put";
                        url = `${clientsApiUrl}/${clientId}/`;
                      } else {
                        data["created_by"] = user.username;
                        method = "post";
                        url = `${clientsApiUrl}/`;
                      }

                      addEditFetch({
                        method,
                        url,
                        data
                      });

                      setSubmitting(false);
                    }}
                  >
                    {props => {
                      const {
                        values,
                        touched,
                        errors,
                        handleChange,
                        handleBlur,
                        handleSubmit
                      } = props;

                      return (
                        <form onSubmit={handleSubmit} autoComplete="off">
                          <div>
                            {clientId ? (
                              <>
                                <h4>Account ID </h4>
                                <span>{clientData.id}</span>
                              </>
                            ) : null}
                          </div>
                          <div>
                            {!allowOidAddEdit ? (
                              <>
                                <h4>
                                  OID{" "}
                                  <Tooltip>
                                    <Content>
                                      <p>
                                        OID can only be edited if no data has
                                        been run for this Account.
                                      </p>
                                    </Content>
                                  </Tooltip>
                                </h4>
                                <span>{values.oid}</span>
                              </>
                            ) : (
                              <TextBox
                                className={styles.textBox}
                                errorText={touched.oid ? errors.oid : null}
                                id="oid"
                                label={
                                  <Label
                                    htmlFor="oid"
                                    error={touched.oid && errors.oid}
                                  >
                                    <b>OID</b>{" "}
                                    <Tooltip>
                                      <Content>
                                        <h3>OID format:</h3>
                                        <ul>
                                          <li>Should Start with 0, 1 or 2.</li>
                                          <li>
                                            Subsequent numbers should be
                                            prefixed with `.` and should not
                                            contain leading 0s.
                                          </li>
                                        </ul>
                                        <p style={{ paddingTop: "2rem" }}>
                                          Example: 2.0.11.256
                                        </p>
                                      </Content>
                                    </Tooltip>
                                  </Label>
                                }
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.oid}
                                width="25%"
                              />
                            )}
                          </div>
                          <div>
                            <TextBox
                              className={styles.textBox}
                              errorText={touched.name ? errors.name : null}
                              id="name"
                              label={
                                <Label
                                  error={touched.name && errors.name}
                                  htmlFor="name"
                                >
                                  <b>Name</b>
                                </Label>
                              }
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.name}
                              width="25%"
                            />
                          </div>
                          <div>
                            <h4>Short Code</h4>
                            {clientId ? (
                              clientData.short_code
                            ) : (
                              <div>
                                <TextBox
                                  className={styles.textBox}
                                  errorText={
                                    touched.shortCode ? errors.shortCode : null
                                  }
                                  id="shortCode"
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.shortCode}
                                  width="25%"
                                />
                              </div>
                            )}
                          </div>
                          <div>
                            <h4>
                              Maintenance Mode{" "}
                              <Tooltip>
                                <Content>
                                  <p>
                                    When enabled, all API requests will be
                                    haulted.
                                  </p>
                                </Content>
                              </Tooltip>
                            </h4>
                            <div>
                              <CheckBox
                                label={maintenanceMode ? "Enabled" : "Disabled"}
                                onChange={() =>
                                  setMaintenanceMode(mode => !mode)
                                }
                                selected={maintenanceMode}
                              />
                            </div>
                          </div>
                          {clientId ? (
                            <>
                              <div>
                                <h4>Created By</h4>
                                <span>{orgData.created_by}</span>
                              </div>
                              <div>
                                <h4>Date Created</h4>
                                <span>
                                  {moment(orgData.created_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                              <div>
                                <h4>Edited By</h4>
                                <span>{orgData.updated_by}</span>
                              </div>
                              <div>
                                <h4>Date Updated</h4>
                                <span>
                                  {moment(orgData.updated_at)
                                    .tz(user.preferences.timeZone)
                                    .format("L, LTS z")}
                                </span>
                              </div>
                            </>
                          ) : null}

                          <ButtonList>
                            <Button
                              primary
                              type="submit"
                              disabled={!isEditVisible(orgData)}
                            >
                              {clientId ? `Save Changes` : `Save`}
                            </Button>
                            <Button onClick={() => history.goBack()}>
                              Cancel
                            </Button>
                          </ButtonList>
                        </form>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </Flex.Item>
          ) : null}
        </Flex>
      </Container>
    </Layout>
  );
};

export default AddEditAccount;
